import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  Paper,
  Button,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  useTheme,
  Chip,
  Autocomplete,
  RadioGroup,
  Radio,
  FormControlLabel, CircularProgress
} from "@mui/material";
import debounce from "lodash/debounce";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { jwtDecode } from "jwt-decode";
import { useAccessToken } from "../../auth/useAccessToken";
import { useApplication } from "../../context/ApplicationContext";
import {
  fetchUserAccess,
  fetchRegionsAndPlants,
  fetchMrpControllers,
  raiseAccessRequest,
  raiseAccessRequestForUsers,
  fetchTeamsAndRoles,
  searchUserName,
  fetchBottlingGroups
} from "../../services/OutboundOrders/UserRolesServices";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PDFDocument, StandardFonts } from "pdf-lib";
import TopSection from "../../CommonComponents/TopSection";
import SectionSpinner from "../../CommonComponents/common/Loader/SectionSpinner";
import { APPLICATION_LIST, APPLICATION_TYPE_MAP } from "../../constants/userAccess";
import { countries } from "countries-list";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessDetails from "./S&OP/AccessDetails";

// First dataset (array of arrays)
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const NDA_CONTENT = ({ today, username }) => [
  <Typography key="p1" variant="body2" paragraph>
    This Non‑Disclosure Agreement <strong>(Agreement)</strong> is entered into
    effective as of <strong>{today}</strong> between{" "}
    <strong>
      The Coca‑Cola Company located at One Coca‑Cola Plaza NW, Atlanta, GA 30313
    </strong>{" "}
    and <strong>{username}</strong>, each of which will be referred to as a
    Party and collectively as Parties.
  </Typography>,

  <Typography key="p2" variant="body2" paragraph>
    The term <strong>“Party”</strong> will include an affiliate of that Party.
    For purposes of this Agreement, an <strong>“Affiliate”</strong> of a Party
    means (i) direct and indirect subsidiaries; (ii) any entity that such Party
    directly or indirectly controls; or (iii) in the case of TCCC, any
    authorized bottler or wholly‑owned subsidiary.
  </Typography>,

  <Typography key="p3" variant="body2" paragraph>
    The Parties may disclose or share with each other information, documents,
    and other confidential or proprietary matters of the disclosing Party{" "}
    <strong>(Information)</strong> in connection with{" "}
    <strong>Confidential Information</strong>.
  </Typography>,

  <Typography key="h1" variant="body2" fontWeight={600} gutterBottom>
    Confidential Information includes:
  </Typography>,

  <ol key="list1" type="a" style={{ paddingLeft: "1.5rem", marginTop: 0 }}>
    <li>
      <Typography variant="body2">
        Information that the disclosing Party marks or designates as
        confidential.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Information that is not marked but would reasonably be considered
        confidential.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        The fact that this Agreement exists and the Parties are cooperating.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Any notes, analyses, summaries, or materials derived from Confidential
        Information.
      </Typography>
    </li>
  </ol>,

  <Typography key="h2" variant="body2" fontWeight={600} gutterBottom>
    Obligations of the Receiving Party:
  </Typography>,

  <ol key="list2" type="a" style={{ paddingLeft: "1.5rem", marginTop: 0 }}>
    <li>
      <Typography variant="body2">
        Restrict disclosure of Confidential Information to employees with a need
        to know.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Not disclose Confidential Information to third parties except as
        permitted.
      </Typography>

      <ol type="i" style={{ paddingLeft: "1.5rem" }}>
        <li>
          <Typography variant="body2">
            Individuals assisting with the stated purpose.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Individuals informed of the confidential nature of the Information.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Individuals bound by confidentiality obligations no less restrictive
            than this Agreement.
          </Typography>
        </li>
      </ol>
    </li>
    <li>
      <Typography variant="body2">
        Use Confidential Information solely for the agreed purpose.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Return or destroy Confidential Information upon written request, except
        one archival copy as required by law.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Promptly notify the disclosing Party of any unauthorized disclosure.
      </Typography>
    </li>
  </ol>,

  <Typography key="p4" variant="body2" paragraph>
    This Agreement is governed by the laws of the{" "}
    <strong>State of Georgia</strong>, and the courts located in{" "}
    <strong>Atlanta, Georgia</strong> shall have exclusive jurisdiction.
  </Typography>,

  <Typography key="p5" variant="body2" paragraph>
    A breach of this Agreement may cause irreparable harm for which monetary
    damages would be insufficient, and injunctive relief may be sought.
  </Typography>,

  <Typography key="p6" variant="body2" paragraph>
    This Agreement represents the <strong>entire agreement</strong> between the
    Parties and supersedes all prior communications or agreements.
  </Typography>,

  <Typography key="p7" variant="body2" paragraph>
    The initial term of this Agreement shall be <strong>three (3) years</strong>
    , renewing annually unless terminated in writing by either Party.
  </Typography>,
];

const NDA_PDF_TEXT = ({ today, username }) => [
  `NON-DISCLOSURE AGREEMENT`,

  `1. This Non-Disclosure Agreement (Agreement) is entered into effective as of ${today} between The Coca-Cola Company located at One Coca-Cola Plaza NW, Atlanta, GA 30313 and ${username}, each of which will be referred to as a Party and collectively as Parties. The term “Party” will include an affiliate of that Party. For purposes of this Agreement, an “Affiliate” of a Party means (i) direct and indirect subsidiaries; (ii) any entity that such Party directly, or indirectly through one or more intermediaries, controls, or is controlled by or is under common control with; or (iii) in the case of TCCC, any bottler with whom TCCC has a license, alliance, or distribution agreement and any wholly-owned subsidiary thereof. The term "control" means ownership of more than 50%, giving an entity the possession, directly or indirectly, of the power to direct or cause the direction of the management and policies of another entity.`,

  `2. The Parties may disclose or share with each other information, documents, and other confidential or proprietary matters of the disclosing Party (Information) in connection with Confidential Information.`,

  `3. The following Information will be considered Confidential Information:`,
  `a. Information that the disclosing Party marks or designates as confidential;`,
  `b. Information that is not marked or designated as being confidential, but that under the circumstances a reasonable person would believe to be confidential;`,
  `c. The fact that this Agreement exists and that the Parties are cooperating with each other for the Purpose; and`,
  `d. Any notes, analyses, compilations, studies, summaries, and other materials, however documented, containing or based, in whole or in part, on any Confidential Information.`,

  `4. Each Party receiving Confidential Information will:`,
  `a. restrict the disclosure of Confidential Information only to its employees who have a need to know the Confidential Information to achieve the Purpose;`,
  `b. not disclose the Confidential Information to any third party; provided however that each Party may disclose Confidential Information to its affiliates, directors, attorneys, and financial advisors who:`,
  `c. only use the Confidential Information for the Purpose;`,
  `d. return all tangible Information (including any copies thereof), and erase all electronic or other intangible forms of the Information, within 30 days of the receipt of a written request from the disclosing Party to do so, provided however that the receiving Party may retain one archival copy to the extent required to comply with its legal obligations; and`,
  `e. give prompt written notice to the disclosing Party of any unauthorized use or disclosure of the Confidential Information, and reasonably assist the disclosing Party in remedying each unauthorized use or disclosure.`,

  `5. The obligations in Section 4 above will not apply to Confidential Information that:`,
  `a. is, or subsequently becomes, available to the public through no breach of the receiving Party’s obligations hereunder;`,
  `b. the receiving Party can show that such Information was previously known to it as a matter of record at the time of receipt;`,
  `c. is subsequently and lawfully obtained from a third party who has obtained the Confidential Information through no breach of the receiving Party’s obligations hereunder; or`,
  `d. is disclosed to a third party by the disclosing Party without a corresponding obligation of confidentiality.`,

  `6. Additionally, a receiving Party will not be in breach of its obligations if it discloses Confidential Information as required by a government agency or by operation of law; provided however that the receiving Party (to the extent permitted by applicable law):`,
  `a. notifies the disclosing Party prior to disclosure to enable the disclosing Party to seek to oppose or restrict the disclosure;`,
  `b. reasonably cooperates with any such attempt by the disclosing Party to oppose or restrict the disclosure or to seek a protective order; and`,
  `c. only discloses such Confidential Information that is required to be disclosed by applicable law.`,

  `7. Each Party agrees not to publish or use any advertising, sales promotion, mailing or publicity matter that mentions or implies the names of the other Party or its affiliates or authorized bottlers without the prior written consent of the other Party.`,

  `8. This Agreement does not constitute a grant of any right or license of any intellectual property rights (e.g., patents, trademarks, copyrights, and trade secrets) of either Party, and each Party will remain the owner of its Confidential Information and all intellectual property rights in its Confidential Information.`,

  `9. No agreement regarding the Purpose will be deemed to exist unless and until a definitive written agreement regarding the Purpose has been signed by each of the Parties. Neither Party is obligated to enter into a definitive written agreement, and neither Party is precluded from considering the same or similar goods or services from third parties. Neither Party will be obligated to:`,
  `a. pay or otherwise provide a benefit to the other Party;`,
  `b. take or refrain from taking any action, except as expressly set forth in this Agreement; or`,
  `c. disclose any Information.`,

  `10. Each disclosing Party represents that it has the lawful right to disclose any Information that it discloses to the receiving Party. Each Party makes no further representations of any kind with respect to any Information that it discloses, and any such Information is provided on an “as is” basis.`,

  `11. This Agreement and its interpretation, and any disputes related to or arising out of this Agreement, will be governed by the laws of the State of Georgia. The courts in the city of Atlanta, Georgia having jurisdiction will adjudicate any such disputes. Each party hereby irrevocably submits to the exclusive jurisdiction of such courts in any such suit, action or proceeding, and each party consents to the venue of such courts.`,

  `12. A breach of this Agreement by a receiving Party will cause the disclosing Party irreparable injury for which monetary damages will not provide adequate compensation and, in addition to any other remedy, the disclosing Party will be entitled to seek injunctive relief against such breach or threatened breach without proving actual damages.`,

  `13. Each Party, in connection with this Agreement, will not engage with:`,
  `a. the government of, or any entity, group or individual within, any country that is the target of any laws administered by the U.S. Office of Foreign Assets Control, Department of the Treasury (OFAC) or any other governmental entity around the world imposing economic sanctions (collectively Embargoed Country); and`,
  `b. any government, entity, group, or individual who is named on the OFAC List of Specially Designated Nationals and Blocked Persons or other similar lists maintained by any governmental entity (collectively Sanctioned Party).`,

  `14. Each Party represents and warrants that it is not:`,
  `a. a Sanctioned Party;`,
  `b. owned or controlled by, or acting for or on behalf of, a Sanctioned Party; or`,
  `c. directly or indirectly owned, controlled by, or acting on behalf of an Embargoed Country.`,
  `Should a Party become designated a Sanctioned Party, or become associated, controlled, or owned by aSanctioned Party or Embargoed Country, any agreement between the Parties will terminate automatically.`,

  `15. Neither Party will be entitled to assign this Agreement to a third party.`,

  `16. This Agreement may be executed in any number of counterparts (including without limitation signature via DocuSign or similar e-signature service or by facsimile transmission), each of which will be deemed an original, but all of which taken together will constitute one single agreement between the Parties. The person signing on behalf of each Party represents that he or she is authorized to execute this Agreement on behalf of such Party and has the authority to bind such Party to the terms and conditions of this Agreement.`,

  `17. This Agreement represents the entire agreement of the Parties and it supersedes all prior communications, agreements, and understandings between the Parties relating to the subject matter hereof. This Agreement may not be modified, amended, or waived except by a written agreement signed by each Party. If a court of competent jurisdiction determines that a provision of this Agreement is unenforceable, this Agreement will be interpreted as if such unenforceable provision is not part of this Agreement.`,

  `18. The initial term of this Agreement will be for a term of three (3) years. Thereafter, the term of this Agreement will renew for successive one-year periods until terminated by either Party. Either Party may terminate this Agreement by providing at least 30 days’ prior written notice to the individual signing on behalf of the other Party. Sections 4, 5, 6, 7 and 8 will survive termination of this Agreement and will continue until the information is no longer considered Confidential Information; provided however that with respect to any Confidential Information that constitutes a trade secret, Sections 4, 5, 6, 7, and 8 will continue to apply until such time as that Confidential Information is no longer protected as a trade secret under applicable law.`,
];

const initialRequestState = {
  applicationType: "",
  region: [],
  plant: [],
  bottlingGroup: [],
  confidentialAccess: [],
  roles: [],
};

const MultiSelectCheckbox = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select one or more",
  width = 200,
}) => {
  const theme = useTheme();
  const safeValue = Array.isArray(value) ? value : [];
  const allSelected = safeValue.length === options.length && options.length > 0;
  const selectAllValue = "SELECT_ALL_OPTION";

  const handleSelectAll = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const optionValues = options.filter((o) => !o.disabled).map((o) => o.value);
    const newSelection = allSelected ? [] : optionValues;
    onChange({ target: { value: newSelection } });
  };

  const handleItemChange = (event) => {
    const selectedValues = event.target.value;
    const isSelectAllToggled = selectedValues.includes(selectAllValue);
    if (isSelectAllToggled) {
      if (allSelected) onChange({ target: { value: [] } });
      else
        onChange({
          target: {
            value: options.filter((o) => !o.disabled).map((o) => o.value),
          },
        });
    } else onChange(event);
  };

  return (
    <FormControl size="small" fullWidth disabled={disabled} sx={{ width }}>
      <InputLabel id={`${label}-label`}>{label}</InputLabel>
      <Select
        labelId={`${label}-label`}
        id={label}
        multiple
        value={Array.isArray(value) ? value : []}
        onChange={handleItemChange}
        label={label}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {placeholder}
              </Typography>
            );
          }

          // Map selected values to their labels
          const selectedLabels = options
            .filter((opt) => selected.includes(opt.value))
            .map((opt) => opt.label);

          if (selectedLabels.length === options.length) {
            return "All Selected";
          }
          if (selectedLabels.length > 2) {
            return `${selectedLabels.slice(0, 2).join(", ")}, +${selectedLabels.length - 2} more`;
          }
          return selectedLabels.join(", ");
        }}
        sx={{
          minWidth: width,
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: width - 30,
          },
        }}
      >
        <MenuItem value={selectAllValue} onClick={handleSelectAll}>
          <Checkbox
            checked={allSelected}
            indeterminate={
              safeValue.length > 0 && safeValue.length < options.length
            }
          />
          <ListItemText
            primary="Select All"
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </MenuItem>
        <Divider />
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            <Checkbox
              checked={safeValue.includes(opt.value)}
              disabled={opt.disabled}
            />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Derived once from the maintained countries-list package (ISO 3166-1)
const countryOptions = Object.entries(countries)
  .map(([code, c]) => ({ code, label: c.name }))
  .sort((a, b) => a.label.localeCompare(b.label));

const teamFilter = createFilterOptions();
const userFilter = createFilterOptions();
const roleFilter = createFilterOptions();

const AL_CONTRACT_MANAGEMENT_ROLES = [
  {
    value: "CPS-AL-ContractAutomation-MOP-Analyst-Group",
    label: "MOP-Analyst",
  },
  {
    value: "CPS-AL-PRICEMEMO-OWNER-CEPG",
    label: "Price Memo Owner (CEPG)",
  },
  {
    value: "CPS-AL-PRICEMEMO-VIEWER-CEPG",
    label: "Price Memo Viewer (CEPG)",
  },
  {
    value: "CPS-AL-CONTRACTAUTOMATION-ANALYST-Group",
    label: "Contract Automation Analyst",
  },
  {
    value: "CPS-AL-PRICEMEMO-OWNER-PACKAGING",
    label: "Price Memo Owner (Packaging)",
  },
  {
    value: "CPS-AL-PRICEMEMO-VIEWER-PACKAGING",
    label: "Price Memo Viewer (Packaging)",
  },
  {
    value: "CM IT Support",
    label: "IT Support",
  },
  {
    value: "CD VIEWER",
    label: "Viewer",
  },
];

const AL_CONTRACT_GLOBAL_ROLES = new Set([
  "CPS-AL-ContractAutomation-MOP-Analyst-Group",
  "CM IT Support",
  "CD VIEWER"
]);

const AL_SUPPLIER_COLLABORATION_ROLES = [
  {
    value: "AL-SupplierCollaboration-Admin-Group",
    label: "App Admin",
  },
  {
    value: "CPS-AL-SupplierCollaboration-Planner-Group::global",
    label: "Global Planner",
  },
  {
    value: "CPS-AL-SupplierCollaboration-Planner-Group",
    label: "Planner",
  },
  {
    value: "CPS-AL-SupplierCollaboration-Viewer-Group::global",
    label: "Global Viewer",
  },
  {
    value: "CPS-AL-SupplierCollaboration-Viewer-Group::regional",
    label: "Viewer",
  },
];

const normalizeSupplierRoleForApi = (roleValue = "") =>
  roleValue.replace("::global", "").replace("::regional", "");

const AL_SUPPLIER_ALL_ACCESS_ROLES = new Set([
  "AL-SupplierCollaboration-Admin-Group",
  "CPS-AL-SupplierCollaboration-Planner-Group::global",
  "CPS-AL-SupplierCollaboration-Viewer-Group::global",
]);

const AL_SUPPLIER_REGIONAL_ROLES = new Set([
  "CPS-AL-SupplierCollaboration-Planner-Group",
  "CPS-AL-SupplierCollaboration-Viewer-Group::regional",
]);

const GRPT_ROLES = [
  {
    value: "CPS-AL-GRPT-Admin-Group",
    label: "Admin",
  },
  {
    value: "CPS-AL-GRPT-Planner-grpt-Group",
    label: "Quality-GRPT",
  },
  {
    value: "CPS-AL-GRPT-Planner-ptf-Group",
    label: "Planner-PTF",
  },
  {
    value: "CPS-AL-GRPT-Quality-aid-Group",
    label: "Quality-AID",
  },
  {
    value: "CPS-AL-GRPT-Approver-Group",
    label: "Approver",
  },
  {
    value: "CPS-AL-GRPT-Viewer-Group",
    label: "Viewer",
  },
];

const GRPT_GLOBAL_ROLES = new Set();

const LEAD_TIME_ANALYTICS_ROLES = [
  {
    value: "CPS-AL-LeadTime-<PLANTNUMBER>-Admin-Group",
    label: "Admin",
  },
  {
    value: "CPS-AL-LeadTime-<PlantNumber>-Approver-Group",
    label: "Approver",
  },
  {
    value: "CPS-AL-LeadTime-<PlantNumber>-Planner-Group",
    label: "Planner",
  },
  {
    value: "CPS-AL-LeadTime-GMP-Group",
    label: "GMP",
  },
  {
    value: "LT IT Support",
    label: "IT Support",
  },
  {
    value: "LT VIEWER",
    label: "Viewer"
  }
];

const LEAD_TIME_GLOBAL_ROLES = new Set([
  "CPS-AL-LeadTime-GMP-Group",
  "LT IT Support",
  "LT VIEWER"
]);

const PO_MAINTENANCE_ROLES = [
  {
    value: "CPS-AL-POAutomation-Admin-Group",
    label: "Admin",
  },
  {
    value: "CPS-AL-POAutomation-Planner-Group",
    label: "Planner",
  },
  {
    value: "CPS-AL-POAutomation-Viewer-Group",
    label: "Viewer",
  },
  // {
  //   value: "CPS-AL-POAutomation-GMP-Group",
  //   label: "GMP",
  // },
  {
    value: "IT Support",
    label: "IT Support",
  }
];

const PO_MAINTENANCE_GLOBAL_ROLES = new Set([
  "CPS-AL-POAutomation-GMP-Group",
  "IT Support",
]);

const INVENTORY_REBALANCING_ROLES = [
  {
    value: "Planner",
    label: "Planner",
  },
  {
    value: "Viewer",
    label: "Viewer",
  },
  {
    value: "IT Support",
    label: "IT Support",
  },
];

const INVENTORY_REBALANCING_GLOBAL_ROLES = new Set(["IT Support"]);

const OM_LOAD_OPTIMISATION_ROLES = [
  {
    value: "Planner",
    label: "Planner",
  },
  {
    value: "Bottler",
    label: "Bottler",
  },
  {
    value: "Dispatcher",
    label: "Dispatcher",
  },
  {
    value: "IT Support",
    label: "IT Support",
  },
];

const DTAI_ROLES = [
  {
    value: "Admin",
    label: "Admin",
  },
  {
    value: "Business User",
    label: "Business User",
  },
  {
    value: "End User",
    label: "End User",
  },
  {
    value: "Platform Support",
    label: "Platform Support",
  },
  {
    value: "SME",
    label: "SME",
  },
  {
    value: "Data Steward",
    label: "Data Steward",
  },
];

// Roles that have no region/plant dropdown for OM - Load Optimization
const OM_TLO_NO_REGION_PLANT_ROLES = new Set([]);

// IT Support gets all regions/plants auto-selected
const OM_TLO_GLOBAL_ROLES = new Set(["IT Support"]);

const OM_TLO_BOTTLER_ROLES = new Set(["Bottler"]);

const resolveRoleNameForApi = (roleValue, roleOptions = []) => {
  const matchedRole = (roleOptions || []).find(
    (roleOption) => roleOption?.value === roleValue,
  );
  return matchedRole?.label || roleValue;
};

const normalizeTeamsAndRolesResponse = (apiData = []) => {
  if (!Array.isArray(apiData)) return [];

  return apiData
    .map((team) => ({
      teamID: team?.teamID ?? team?.TeamID ?? null,
      teamName: team?.teamName ?? team?.TeamName ?? "",
      roles: (team?.roles ?? team?.Roles ?? [])
        .map((role) => {
          if (typeof role === "string") {
            return { roleName: role };
          }
          return {
            roleName:
              role?.roleName ??
              role?.RoleName ??
              role?.name ??
              role?.role ??
              "",
          };
        })
        .filter((role) => role.roleName),
    }))
    .filter((team) => team.teamName);
};

const AccessRequestForm = () => {
  const breadcrumbArray = ["User Access Management", "New Request"];
  const [requestedFor, setRequestedFor] = useState("");
  const [requestedForUserName, setRequestedForUserName] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [requestForType, setRequestForType] = useState("self");
  const [currentRequest, setCurrentRequest] = useState(initialRequestState);
  const [fullyAccessibleRegions, setFullyAccessibleRegions] = useState([]);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileName, setFileName] = useState("");
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [ndaDecision, setNdaDecision] = useState("Not Accepted");
  const [openNdaModal, setOpenNdaModal] = useState(false);
  const [ndaForm, setNdaForm] = useState({
    effectiveDate: "",
    disclosingParty: "The Coca-Cola Company",
    receivingParty: "",
    purposeOfDisclosure: "",
    partyLegalName: "",
    signBy: "",
    signName: "",
    signTitle: "",
    signDate: "",
    noticeAddress: "",
    noticeEmail: "",
  });
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("");
  //const [userAccessResp, setUserAccessResp] = useState(null);
  const [decoded, setDecoded] = useState(null);
  const [showPreviewSummary, setShowPreviewSummary] = useState(false);
  const [previewSummary, setPreviewSummary] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  // ── New fields: Country, Team, Roles ───────────────────────────────────────
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [teamsRolesData, setTeamsRolesData] = useState([]);
  const [teamInputValue, setTeamInputValue] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null); // object (existing) | string (new)
  const [selectedRole, setSelectedRole] = useState(null); // object (existing) | string (new)
  const [selectedStaticTeam, setSelectedStaticTeam] = useState("");
  // ──────────────────────────────────────────────────────────────────────────

  const [fieldsData, setFieldsData] = useState({
    applications: [],
    regions: [],
    plants: [],
    bottlingGroups: [],
    confidentialFields: [],
    roles: [],
  });
  const INPUT_WIDTH = 450;

  const STATIC_TEAM_OPTIONS = [
    { teamID: 1, teamName: "Inventory Optimization", roles: [] },
    { teamID: 2, teamName: "Supply Planning", roles: [] },
  ];
  const accessToken = useAccessToken();
  const { selectedApplication, setSelectedApplication } = useApplication();
  const today = new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  const {
    application,
    region,
    plant,
    /*mrpController,*/ confidentialAccess,
    roles,
  } = currentRequest;

  const fetchUsersByName = async (searchText) => {
    // if (!searchText || searchText.length < 2) {
    //   //setUserOptions([]);
    //   return;
    // }
    setUserLoading(true);
    try {
      const users = await searchUserName(searchText);
      setUserOptions(Array.isArray(users) ? users : []);
    } catch (e) {
      console.error("User search failed", e);
      setUserOptions([]);
    } finally {
      setUserLoading(false);
    }
  };

  const debouncedFetchUsers = useMemo(
    () => debounce(fetchUsersByName, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [debouncedFetchUsers]);

  useEffect(() => {
    setSelectedApplication(currentRequest.applicationType);
  }, [currentRequest.applicationType]);

  useEffect(() => {
  if (selectedApplication) {
    setCurrentRequest((prev) => ({
      ...prev,
      applicationType: selectedApplication,
    }));
  }
}, []);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    try {
      const parsed = jwtDecode(accessToken);
      setDecoded(parsed);
    } catch (err) {
      console.error("Failed to decode token:", err);
    }
  }, [accessToken]);

  useEffect(() => {
    const fetchRegionsAndPlantsMappings = async () => {
      const ACCESS_DATA = {
        applications: APPLICATION_LIST,
        regions: [],
        plants: {},
        confidentialFields: [
          { value: "BOM", label: "BOM" },
          { value: "MATERIAL MASTER", label: "MATERIAL MASTER" },
          { value: "FINANCE", label: "FINANCE" },
          { value: "INVENTORY", label: "INVENTORY & WAREHOUSE" },
          { value: "WH-ADMIN", label: "WH ADMIN" }
        ],
        roles: [
          { value: "Business Super User", label: "Business Super User" },
          { value: "IT Support", label: "IT Support" },
          { value: "SSA Admin", label: "SSA Admin" },
          { value: "Approver", label: "Approver" },
          { value: "Planner", label: "Planner" },
        ],
      };
      // try {
      //   const regionsAndPlantsMapping = await fetchRegionsAndPlants();
      //   const userAccessData = await fetchUserAccess();

      //   // Only conditionally populate regions/plants
      //   if (
      //     Array.isArray(regionsAndPlantsMapping) &&
      //     regionsAndPlantsMapping.length > 0
      //     //&& userAccessData?.regionPlantMapping

      //   ) {
      //     setUserAccessResp(userAccessData);

      //     const regionSet = new Set();
      //     regionsAndPlantsMapping.forEach(([plantFullName, region]) => {
      //       regionSet.add(region);
      //       if (!ACCESS_DATA.plants[region]) {
      //         ACCESS_DATA.plants[region] = [];
      //       }
      //       ACCESS_DATA.plants[region].push({
      //         value: plantFullName,
      //         label: plantFullName,
      //       });
      //     });
      //     ACCESS_DATA.regions = Array.from(regionSet).map((regionName) => ({
      //       value: regionName,
      //       label: regionName,
      //     }));
      //   }
      // } catch (err) {
      //   console.error("API failed:", err);
      // }
      // try {
      //   // always fetch regions/plants
      //   const regionsAndPlantsMapping = await fetchRegionsAndPlants();

      //   if (Array.isArray(regionsAndPlantsMapping) && regionsAndPlantsMapping.length > 0) {
      //     const regionSet = new Set();

      //     regionsAndPlantsMapping.forEach(([plantFullName, region]) => {
      //       regionSet.add(region);
      //       if (!ACCESS_DATA.plants[region]) {
      //         ACCESS_DATA.plants[region] = [];
      //       }
      //       ACCESS_DATA.plants[region].push({
      //         value: plantFullName,
      //         label: plantFullName,
      //       });
      //     });

      //     ACCESS_DATA.regions = Array.from(regionSet).map((r) => ({
      //       value: r,
      //       label: r,
      //     }));
      //   }
      // } catch (err) {
      //   console.error("fetchRegionsAndPlants failed:", err);
      // }
      // fetch user access separately (non-blocking)
      // try {
      //   const userAccessData = await fetchUserAccess();
      //   setUserAccessResp(userAccessData);
      // } catch (err) {
      //   console.warn("fetchUserAccess failed (non-blocking):", err);
      // }
      try {
        let regionsAndPlantsMapping = [];

        const isExistingFlow =
          currentRequest.applicationType === "IO - Safety Stock Approval" ||
          currentRequest.applicationType === "Command Center";

        if (isExistingFlow) {
          // Existing API flow
          regionsAndPlantsMapping = await fetchRegionsAndPlants(
            APPLICATION_TYPE_MAP[currentRequest.applicationType]
          );
        } else {
          regionsAndPlantsMapping = await fetchRegionsAndPlants(APPLICATION_TYPE_MAP
          [currentRequest.applicationType]);
        }

        if (
          Array.isArray(regionsAndPlantsMapping) &&
          regionsAndPlantsMapping.length > 0
        ) {
          const regionSet = new Set();

          // Command Center -> only ctEnabled true
          // Other apps -> all records
          const filteredMappings =
            currentRequest.applicationType === "Command Center"
              ? regionsAndPlantsMapping.filter((item) => item.ctEnabled)
              : regionsAndPlantsMapping;

          filteredMappings.forEach((item) => {
            const region = item.region;
            const plantName = item.plantName;

            // skip invalid/null region
            if (!region) return;

            regionSet.add(region);

            if (!ACCESS_DATA.plants[region]) {
              ACCESS_DATA.plants[region] = [];
            }

            ACCESS_DATA.plants[region].push({
              value: plantName,
              label: plantName,
            });
          });

          ACCESS_DATA.regions = Array.from(regionSet).map((r) => ({
            value: r,
            label: r,
          }));
        }
      } catch (err) {
        console.error("fetchRegionsAndPlants failed:", err);
      }
      setFieldsData(ACCESS_DATA);
    };
    fetchRegionsAndPlantsMappings();
  }, [currentRequest.applicationType]);

  const availablePlants = useMemo(() => {
    if (region.length === 0 || !fieldsData.plants) return [];
    let plants = [];

    // If all regions are selected, return all plants and mark as disabled
    const allRegionValues = (fieldsData.regions || []).map((r) => r.value);
    const allSelected =
      region.length === allRegionValues.length && allRegionValues.length > 0;
    if (allSelected) {
      const disablePlantsForAllRegions =
        currentRequest.applicationType === "IO - Safety Stock Approval";
      plants = Object.values(fieldsData.plants)
        .flat()
        .map((p) => ({ ...p, disabled: disablePlantsForAllRegions }));
    } else {
      plants = region.flatMap((r) =>
        (fieldsData.plants[r] || []).map((p) => ({ ...p, disabled: false })),
      );
    }
    return plants;
  }, [region, fieldsData.plants, fieldsData.regions]);

  // Cascading roles: filter by selected team for pre-populated teams;
  // for new (free-text) teams, surface all distinct roles as suggestions.
  const availableRoles = useMemo(() => {
    if (!selectedTeam) return [];
    if (typeof selectedTeam === "string") {
      // New team entered – no cascading; show all known roles as suggestions
      const seen = new Set();
      return teamsRolesData
        .flatMap((t) => t.roles || [])
        .filter((r) => !seen.has(r.roleName) && seen.add(r.roleName));
    }
    // Pre-populated team – return only that team's roles
    if (Array.isArray(selectedTeam?.roles) && selectedTeam.roles.length > 0) {
      return selectedTeam.roles;
    }

    const team = teamsRolesData.find(
      (t) =>
        (selectedTeam?.teamID != null &&
          String(t.teamID) === String(selectedTeam.teamID)) ||
        t.teamName === selectedTeam?.teamName,
    );
    return team?.roles || [];
  }, [selectedTeam, teamsRolesData]);

  // Whenever fieldsData updates, clear region/plant selections so dropdown recalcs
  // useEffect(() => {
  //   setCurrentRequest((prev) => ({
  //     ...prev,
  //     region: [],
  //     plant: [],
  //   }));
  // }, [fieldsData]);

  // Reset confidentialAccess when switching application type or NDA is not accepted
  useEffect(() => {
    setCurrentRequest((prev) => {
      // Only keep confidentialAccess if Command Center and NDA accepted
      if (prev.applicationType === "Command Center" && ndaAccepted) {
        return prev;
      }
      return { ...prev, confidentialAccess: [] };
    });
  }, [currentRequest.applicationType, ndaAccepted]);

  useEffect(() => {
    const loadTeamsAndRoles = async () => {
      try {
        const data = await fetchTeamsAndRoles();
        const normalized = normalizeTeamsAndRolesResponse(data);
        setTeamsRolesData(normalized);
      } catch (err) {
        console.error("Error fetching Teams and Roles:", err);
      }
    };
    loadTeamsAndRoles();
  }, []);

  const email = decoded?.unique_name || "";
  const username = decoded
    ? `${decoded.given_name || ""} ${decoded.family_name || ""}`.trim()
    : "";

  const isSafetyStockLikeApp =
    currentRequest.applicationType === "IO - Safety Stock Approval" ||
    currentRequest.applicationType === "CS - Inventory Rebalancing" ||
    currentRequest.applicationType === "MP - Lead time Analytics" ||
    currentRequest.applicationType === "MP - AL Contract Automation" ||
    currentRequest.applicationType === "MP - AL Supplier Collaboration/Confirmation" ||
    currentRequest.applicationType === "MP - AL Supplier Collaboration" ||
    currentRequest.applicationType === "MP - Supplier Confirmation" ||
    currentRequest.applicationType === "MP - GR Processing Time Analytics" ||
    currentRequest.applicationType === "MP - AL PO Management" ||
    currentRequest.applicationType === "OM - Load Optimization" ||
    currentRequest.applicationType === "Data Trust AI";

  const isDataTrustAI = currentRequest.applicationType === "Data Trust AI";
  const isDataTrustAIAdmin = isDataTrustAI && (currentRequest.roles?.[0] || "") === "Admin"
  const isSopApp = currentRequest.applicationType === "S&OP - Sales And Operations Planning";

  const isAlContractManagementApp =
    currentRequest.applicationType === "MP - AL Contract Automation";

  const isAlSupplierCollaborationApp =
    currentRequest.applicationType ===
    "MP - AL Supplier Collaboration/Confirmation" ||
    currentRequest.applicationType === "MP - AL Supplier Collaboration" ||
    currentRequest.applicationType === "MP - Supplier Confirmation";

  const hidePlantDropdownForAlSupplier = false;

  const isLeadTimeApp =
    currentRequest.applicationType === "MP - Lead time Analytics";

  const isPoMaintenanceApp =
    currentRequest.applicationType === "MP - AL PO Management";

  const isIoSafetyStockApp =
    currentRequest.applicationType === "IO - Safety Stock Approval";

  const isInventoryRebalancingApp =
    currentRequest.applicationType === "CS - Inventory Rebalancing";

  const selectedAlRole = currentRequest.roles?.[0] || "";
  const isAlContractGlobalRole =
    isAlContractManagementApp && AL_CONTRACT_GLOBAL_ROLES.has(selectedAlRole);

  const canSelectPlantsManuallyForAl =
    !isAlContractManagementApp ||
    !isAlContractGlobalRole;

  const isAlSupplierAllAccessRole =
    isAlSupplierCollaborationApp &&
    AL_SUPPLIER_ALL_ACCESS_ROLES.has(selectedAlRole);

  const isAlSupplierRegionalRole =
    isAlSupplierCollaborationApp &&
    AL_SUPPLIER_REGIONAL_ROLES.has(selectedAlRole);

  const isGrptAllAccessRole =
    currentRequest.applicationType === "MP - GR Processing Time Analytics" &&
    GRPT_GLOBAL_ROLES.has(selectedAlRole);

  const isGrptRegionalAutoPlantsRole = false;

  const isLeadTimeGlobalRole =
    isLeadTimeApp && LEAD_TIME_GLOBAL_ROLES.has(selectedAlRole);

  const isPoMaintenanceGlobalRole =
    isPoMaintenanceApp && PO_MAINTENANCE_GLOBAL_ROLES.has(selectedAlRole);

  const isInventoryRebalancingGlobalRole =
    isInventoryRebalancingApp &&
    INVENTORY_REBALANCING_GLOBAL_ROLES.has(selectedAlRole);

  const isOmTruckLoadApp =
    currentRequest.applicationType === "OM - Load Optimization";

  const isOmTloNoRegionPlantRole =
    isOmTruckLoadApp && OM_TLO_NO_REGION_PLANT_ROLES.has(selectedAlRole);

  const isOmTloGlobalRole =
    isOmTruckLoadApp && OM_TLO_GLOBAL_ROLES.has(selectedAlRole);

  const isOmTloBottlerRole =
    isOmTruckLoadApp && OM_TLO_BOTTLER_ROLES.has(selectedAlRole);

  const activeRoleOptions =
    currentRequest.applicationType === "MP - AL Contract Automation"
      ? AL_CONTRACT_MANAGEMENT_ROLES
      : currentRequest.applicationType === "MP - AL Supplier Collaboration" ||
        currentRequest.applicationType ===
        "MP - AL Supplier Collaboration/Confirmation" ||
        currentRequest.applicationType === "MP - Supplier Confirmation"
        ? AL_SUPPLIER_COLLABORATION_ROLES
        : currentRequest.applicationType === "MP - GR Processing Time Analytics"
          ? GRPT_ROLES
          : currentRequest.applicationType === "MP - Lead time Analytics"
            ? LEAD_TIME_ANALYTICS_ROLES
            : currentRequest.applicationType === "MP - AL PO Management"
              ? PO_MAINTENANCE_ROLES
              : currentRequest.applicationType === "CS - Inventory Rebalancing"
                ? INVENTORY_REBALANCING_ROLES
                : currentRequest.applicationType === "OM - Load Optimization"
                  ? OM_LOAD_OPTIMISATION_ROLES
                  : currentRequest.applicationType === "Data Trust AI"
                    ? DTAI_ROLES
                    : fieldsData.roles || [];

  // Determine if Admin role is selected (from Team & Role Autocomplete or Safety Stock role)
  const isAdminRole = (() => {
    const adminRoles = ["it support", "cm it support", "lt it support", "ssa admin", "lt viewer", "cd viewer"];
    const roleName =
      typeof selectedRole === "string"
        ? selectedRole
        : selectedRole?.roleName || "";
    if (adminRoles.includes(roleName.trim().toLowerCase())) return true;
    if (
      currentRequest.applicationType === "IO - Safety Stock Approval" &&
      currentRequest.roles?.[0] &&
      adminRoles.includes(currentRequest.roles[0].trim().toLowerCase())
    )
      return true;
    return false;
  })();

  useEffect(() => {
    const loadBottlingGroups = async () => {
      try {
        if (
          !isOmTloBottlerRole ||
          region.length === 0 ||
          plant.length === 0
        ) {
          setFieldsData((prev) => ({
            ...prev,
            bottlingGroups: [],
          }));

          setCurrentRequest((prev) => ({
            ...prev,
            bottlingGroup: [],
          }));
          return;
        }

        const plantCodes = plant.map((p) => p.split("-")[0]);
        const response = await fetchBottlingGroups(region, plantCodes);

        const formattedGroups = (
          response?.plantBottlingGroups || []
        ).map((item) => ({
          value: item,
          label: item,
        }));
        setFieldsData((prev) => ({
          ...prev,
          bottlingGroups: formattedGroups,
        }));

        if (response?.message) {
          setSnackbarMessage(response.message);
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        }
      } catch (err) {
        console.error("Error fetching bottling groups:", err);
        setFieldsData((prev) => ({
          ...prev,
          bottlingGroups: [],
        }));
        setSnackbarMessage("Failed to fetch bottling groups");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    };
    loadBottlingGroups();
  }, [plant, isOmTloBottlerRole]);

  // Auto-select all regions/plants for Admin; reset access level on any role change
  useEffect(() => {
    if (!fieldsData.regions || !fieldsData.plants) return;
    if (!selectedRole) return;
  }, [selectedRole]);

  useEffect(() => {
    if (!isAlContractGlobalRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [isAlContractGlobalRole, fieldsData.regions, fieldsData.plants]);

  useEffect(() => {
    if (!isAlContractManagementApp || canSelectPlantsManuallyForAl) return;

    setCurrentRequest((prev) => {
      const allPlantsForSelectedRegions = (prev.region || []).flatMap(
        (regionName) =>
          (fieldsData.plants?.[regionName] || []).map((plantObj) => plantObj.value),
      );

      return {
        ...prev,
        plant: allPlantsForSelectedRegions,
      };
    });
  }, [
    isAlContractManagementApp,
    canSelectPlantsManuallyForAl,
    currentRequest.region,
    fieldsData.plants,
  ]);

  useEffect(() => {
    if (!isAlSupplierAllAccessRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [isAlSupplierAllAccessRole, fieldsData.regions, fieldsData.plants]);

  useEffect(() => {
    if (!isAlSupplierRegionalRole) return;

    setCurrentRequest((prev) => {
      const allPlantsForSelectedRegions = (prev.region || []).flatMap(
        (regionName) =>
          (fieldsData.plants?.[regionName] || []).map((plantObj) => plantObj.value),
      );

      return {
        ...prev,
        plant: allPlantsForSelectedRegions,
      };
    });
  }, [isAlSupplierRegionalRole, currentRequest.region, fieldsData.plants]);

  useEffect(() => {
    if (!isGrptAllAccessRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [isGrptAllAccessRole, fieldsData.regions, fieldsData.plants]);

  useEffect(() => {
    if (!isGrptRegionalAutoPlantsRole) return;

    setCurrentRequest((prev) => {
      const allPlantsForSelectedRegions = (prev.region || []).flatMap(
        (regionName) =>
          (fieldsData.plants?.[regionName] || []).map((plantObj) => plantObj.value),
      );

      return {
        ...prev,
        plant: allPlantsForSelectedRegions,
      };
    });
  }, [isGrptRegionalAutoPlantsRole, currentRequest.region, fieldsData.plants]);

  useEffect(() => {
    if (!isLeadTimeGlobalRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [isLeadTimeGlobalRole, fieldsData.regions, fieldsData.plants]);

  useEffect(() => {
    if (!isPoMaintenanceGlobalRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [isPoMaintenanceGlobalRole, fieldsData.regions, fieldsData.plants]);

  useEffect(() => {
    if (!isInventoryRebalancingGlobalRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [
    isInventoryRebalancingGlobalRole,
    fieldsData.regions,
    fieldsData.plants,
  ]);

  useEffect(() => {
    if (!isOmTloGlobalRole) return;

    const allRegions = (fieldsData.regions || []).map((r) => r.value);
    const allPlants = Object.values(fieldsData.plants || {})
      .flat()
      .map((p) => p.value);

    setCurrentRequest((prev) => ({
      ...prev,
      region: allRegions,
      plant: allPlants,
    }));
  }, [isOmTloGlobalRole, fieldsData.regions, fieldsData.plants]);
  useEffect(() => {
    if (!isDataTrustAI) return;
    if (isDataTrustAIAdmin) {
      const allRegions = (fieldsData.regions || []).map(r => r.value)
      const allPlants = Object.values(fieldsData.plants || {}).flat().map(p => p.value)
      setCurrentRequest(prev => ({
        ...prev,
        region: allRegions,
        plant: allPlants
      }))
      setSelectedStaticTeam("")
    }
  }, [isDataTrustAIAdmin, isDataTrustAI, fieldsData.regions, fieldsData.plants])
  // Hide preview whenever any form value changes so user must click Preview again for updated data
  useEffect(() => {
    setShowPreviewSummary(false);
  }, [
    currentRequest,
    selectedCountry,
    selectedTeam,
    selectedRole,
    ndaAccepted,
    requestForType,
    requestedFor,
    requestedForUserName,
  ]);

  const onBehalfInfoMessage =
    "This section is only for submitting access requests on behalf of another user. You cannot enter your own username or email.";

  const normalizeNameForCompare = (value = "") =>
    value.trim().replace(/\s+/g, " ").toLowerCase();

  const isSelfRequestedFor =
    (requestedFor.trim().length > 0 &&
      requestedFor.trim().toLowerCase() === email.trim().toLowerCase()) ||
    (requestedForUserName.trim().length > 0 &&
      normalizeNameForCompare(requestedForUserName) ===
      normalizeNameForCompare(username));

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleRequestedForChange = (e) => {
    const value = e.target.value;
    // If user is doing it for self (field is empty), set to null, else set entered value
    setRequestedFor(value);
    setCurrentRequest((prev) => ({
      ...prev,
      requestedFor: value.trim().length === 0 ? null : value,
    }));

    // Email format validation
    if (value.trim().length > 0 && !isValidEmail(value.trim())) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }

    // Show snackbar if user enters their own email
    if (
      value.trim().length > 0 &&
      value.trim().toLowerCase() === email.trim().toLowerCase()
    ) {
      setSnackbarMessage(onBehalfInfoMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }
  };

  const handleRequestedForUserNameChange = (e) => {
    const value = e.target.value;
    // If user is doing it for self (field is empty), set to null, else set entered value
    setRequestedForUserName(value);
    setCurrentRequest((prev) => ({
      ...prev,
      requestedForUserName: value.trim().length === 0 ? null : value,
    }));

    if (
      value.trim().length > 0 &&
      normalizeNameForCompare(value) === normalizeNameForCompare(username)
    ) {
      setSnackbarMessage(onBehalfInfoMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    setRequestedForUserName(value);
  };

  const formatValue = (value, fallback, allOptions) => {
    const hasObjectOptions =
      Array.isArray(allOptions) &&
      allOptions.length > 0 &&
      typeof allOptions[0] === "object";

    const toLabel = (item) => {
      if (!hasObjectOptions) return item;
      const matched = allOptions.find((opt) => opt?.value === item);
      return matched?.label || item;
    };

    if (Array.isArray(value)) {
      const labeledValues = value.map(toLabel);
      if (labeledValues.length === 0) return fallback;
      else if (allOptions && value.length === allOptions.length)
        return `All ${fallback.replace("No ", "")}s`;
      else if (labeledValues.length > 3)
        return `${labeledValues.slice(0, 3).join(", ")}, +${labeledValues.length - 3} more`;
      return labeledValues.join(", ");
    }
    return toLabel(value) || fallback;
  };

  const formatRoleLabels = (roleValues, roleOptions, fallback = "No Role") => {
    const safeRoles = Array.isArray(roleValues)
      ? roleValues.filter(Boolean)
      : roleValues
        ? [roleValues]
        : [];

    if (safeRoles.length === 0) return fallback;

    const labels = safeRoles.map((roleValue) => {
      const matchedRole = (roleOptions || []).find(
        (roleOption) => roleOption?.value === roleValue,
      );
      return matchedRole?.label || roleValue;
    });

    return labels.join(", ");
  };

  const generateSummary = (req) => {
    const appType = req.applicationType || "Command Center";
    const isOmApp = appType === "OM - Load Optimization";
    const selectedOmRole = req.roles?.[0] || "";
    const omNoRegionPlant = isOmApp && OM_TLO_NO_REGION_PLANT_ROLES.has(selectedOmRole);

    let summary = `--- User Details ---`;
    if (requestForType === "other") {
      summary += `\n- Name: ${requestedForUserName || "N/A"}`;
      summary += `\n- Email: ${requestedFor || "N/A"}`;
      summary += `\n- Requested By: ${username || "N/A"} (${email || "N/A"})`;
    } else {
      summary += `\n- Name: ${username || "N/A"}`;
      summary += `\n- Email: ${email || "N/A"}`;
    }
    summary += `\n\nAccess requested for Application: **${appType}**`;

    if (appType !== "IO - Safety Stock Approval" && appType !== "MP - AL Contract Automation" && !isOmApp) {
      // Team
      let teamLabel = "No Team";
      if (selectedTeam) {
        teamLabel =
          typeof selectedTeam === "string"
            ? selectedTeam
            : selectedTeam.teamName || "No Team";
      }
      summary += `\n- Team: ${teamLabel}`;
      // Role
      let roleLabel = "No Role";
      if (selectedRole) {
        roleLabel =
          typeof selectedRole === "string"
            ? selectedRole
            : selectedRole.roleName || "No Role";
      }
      summary += `\n- Role: ${roleLabel}`;
      if (appType === "Data Trust AI") {
        summary += `\n- Team: ${selectedStaticTeam || "No Team"}`;
      }
    } else {
      // For Safety Stock-like apps and OM, show the single selected role
      summary += `\n\n--- Role ---`;

      const roleOptionsForSummary =
        appType === "MP - AL Contract Automation"
          ? AL_CONTRACT_MANAGEMENT_ROLES
          : appType === "MP - AL Supplier Collaboration" ||
            appType === "MP - AL Supplier Collaboration/Confirmation" ||
            appType === "MP - Supplier Confirmation"
            ? AL_SUPPLIER_COLLABORATION_ROLES
            : appType === "MP - GR Processing Time Analytics"
              ? GRPT_ROLES
              : appType === "MP - Lead time Analytics"
                ? LEAD_TIME_ANALYTICS_ROLES
                : appType === "MP - AL PO Management"
                  ? PO_MAINTENANCE_ROLES
                  : appType === "CS - Inventory Rebalancing"
                    ? INVENTORY_REBALANCING_ROLES
                    : appType === "OM - Load Optimization"
                      ? OM_LOAD_OPTIMISATION_ROLES
                      : appType === "Data Trust AI"
                        ? DTAI_ROLES
                        : fieldsData.roles;
      summary += `\n- Role: ${formatRoleLabels(req.roles, roleOptionsForSummary, "No Role")}`;
    }

    summary += `\n\n--- Access Level ---`;

    if (omNoRegionPlant) {
      summary += `\n- Region(s): N/A (not required for this role)`;
      summary += `\n- Plant(s): N/A (not required for this role)`;
    } else {
      summary += `\n- Region(s): ${formatValue(req.region, "No Region", fieldsData.regions)}`;
      summary += `\n- Plant(s): ${formatValue(req.plant, "No Plant")}`;
    }

    // Confidential Access Level: only if not Safety Stock-like or OM app
    if (appType !== "IO - Safety Stock Approval" && appType !== "MP - AL Contract Automation" && !isOmApp) {
      summary += `\n\n--- Confidential Access Level ---`;
      summary += `\n- NDA Status: ${ndaDecision}`;
      const confStr = formatValue(
        req.confidentialAccess,
        "None",
        fieldsData.confidentialFields,
      );
      summary += `\n- Fields: ${confStr}`;
    }
    return summary;
  };

  const handleFileDownload = () => {
    const fileUrl = "/files/TCCC-Form_Of_NDA(23June2022).docx";
    const element = document.createElement("a");
    element.href = fileUrl;
    element.download = "TCCC-Form_Of_NDA(23June2022).docx";
    document.body.appendChild(element); // Append the link to the DOM
    element.click(); // Programmatically trigger click
    document.body.removeChild(element); // Cleanup
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileUploaded(true);
      setFileName(file.name);
      setSnackbarMessage("File uploaded successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSignatureFile(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const handleNdaChange = (field) => (e) => {
    setNdaForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleMultiSelectChange = (field) => (e) => {
    const selectedValues = Array.isArray(e.target.value) ? e.target.value : [];
    setCurrentRequest((prev) => {
      const newRequest = { ...prev, [field]: selectedValues };
      if (field === "region") {
        // If all regions are selected, auto-select all plants
        const allRegionValues = (fieldsData.regions || []).map((r) => r.value);
        if (selectedValues.length === allRegionValues.length) {
          const allPlants = Object.values(fieldsData.plants || {})
            .flat()
            .map((p) => p.value);
          newRequest.plant = allPlants;
        } else if (isGrptRegionalAutoPlantsRole) {
          newRequest.plant = selectedValues.flatMap((regionName) =>
            (fieldsData.plants[regionName] || []).map((p) => p.value),
          );
        } else if (isAlSupplierRegionalRole) {
          newRequest.plant = selectedValues.flatMap((regionName) =>
            (fieldsData.plants[regionName] || []).map((p) => p.value),
          );
        } else if (
          isAlContractManagementApp &&
          AL_CONTRACT_GLOBAL_ROLES.has(prev.roles?.[0] || "")
        ) {
          newRequest.plant = selectedValues.flatMap((regionName) =>
            (fieldsData.plants[regionName] || []).map((p) => p.value),
          );
        } else {
          // Keep only plants that belong to the still-selected regions
          const validPlants = new Set(
            selectedValues.flatMap((r) =>
              (fieldsData.plants[r] || []).map((p) => p.value),
            ),
          );
          newRequest.plant = (prev.plant || []).filter((p) =>
            validPlants.has(p),
          );
        }
        //newRequest.mrpController = [];
      } else if (field === "plant") {
        //newRequest.mrpController = [];
      }
      return newRequest;
    });
  };

  const handleAccessDetails = (data) => {
    console.log("Access details received:", data);
  }

  const handlePreview = async () => {
    //if (application.length === 0) return;
    const summary = generateSummary(currentRequest);
    setShowPreviewSummary(true);
    setPreviewSummary(summary);
  };

  const normalizeText = (text) =>
    text
      .replace(/\s+/g, " ") // collapse all whitespace
      .replace(/\n/g, " ") // remove line breaks
      .trim();

  const getInitials = (fullName = "") =>
    fullName
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0].toUpperCase())
      .join("");

  const generateNdaPdf = async () => {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    const pageSize = [595, 842];
    const marginX = 50;
    const maxWidth = 495;
    const fontSize = 10;
    const lineHeight = 14;
    let pages = [];
    let page = pdfDoc.addPage(pageSize);
    pages.push(page);
    const { height } = page.getSize();
    let y = height - 50;

    const initials = getInitials(username);

    const drawFooter = (page, text) => {
      page.drawText(text, {
        x: marginX,
        y: 30,
        size: 9,
        font,
      });
    };

    const drawParagraph = (rawText, isTitle = false) => {
      const text = normalizeText(rawText);
      const size = isTitle ? 12 : fontSize;
      const spacing = isTitle ? 20 : lineHeight;

      const words = text.split(" ");
      let line = "";

      for (const word of words) {
        const testLine = line + word + " ";
        const width = font.widthOfTextAtSize(testLine, size);

        if (width > maxWidth) {
          if (y < 60) {
            page = pdfDoc.addPage(pageSize);
            pages.push(page);
            y = height - 50;
          }

          page.drawText(line.trim(), { x: marginX, y, size, font });
          y -= spacing;
          line = word + " ";
        } else {
          line = testLine;
        }
      }

      if (line.trim()) {
        if (y < 60) {
          page = pdfDoc.addPage(pageSize);
          pages.push(page);
          y = height - 50;
        }

        page.drawText(line.trim(), { x: marginX, y, size, font });
        y -= spacing;
      }

      y -= 10;
    };

    // ✅ NDA body
    NDA_PDF_TEXT({ today, username }).forEach((text, index) => {
      drawParagraph(text, index === 0);
    });

    y -= 30;

    // ✅ Signature block (LAST PAGE)
    drawParagraph("Accepted By:");
    drawParagraph(`Name: ${username}`);
    drawParagraph(`Email: ${email}`);
    drawParagraph(`Date: ${today}`);
    drawParagraph("Status: NDA ACCEPTED");

    // ✅ Footer logic
    pages.forEach((p, index) => {
      const isLastPage = index === pages.length - 1;

      if (isLastPage) {
        drawFooter(p, `${username} | ${today}`);
      } else {
        drawFooter(p, `Initials: ${initials}`);
      }
    });

    return await pdfDoc.save();
  };

  const previewNdaPdf = async () => {
    try {
      const pdfBytes = await generateNdaPdf();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank");
    } catch (err) {
      console.error("Error previewing NDA PDF:", err);
    }
  };

  const handleSave = async () => {
    if (isSelfRequestedFor) {
      setSnackbarMessage(onBehalfInfoMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    // Validation and payload fields per application type
    if (currentRequest.applicationType === "Command Center") {
      if (!selectedCountry) {
        setSnackbarMessage("Please select a Country before submitting");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
      if (!selectedTeam) {
        setSnackbarMessage("Please select or enter a Team before submitting");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
      if (
        !selectedRole ||
        (typeof selectedRole === "string" && selectedRole.trim().length === 0)
      ) {
        setSnackbarMessage("Please select or enter a Role before submitting");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
    } else if (isSafetyStockLikeApp) {
      // Only require a role for Safety Stock Approval and AL Contract Automation

      if (!currentRequest.roles || !currentRequest.roles[0]) {
        setSnackbarMessage("Please select a Role before submitting");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }

      if (isDataTrustAI && !isDataTrustAIAdmin && !selectedStaticTeam) {
        setSnackbarMessage("Please select a Team before submitting");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
    }
    setSubmitting(true);
    try {
      let payload = {
        email,
        requestedFor: requestedFor ?? null,
        requestedForUserName: requestedForUserName ?? null,
        applicationType: APPLICATION_TYPE_MAP[currentRequest.applicationType],
        regions: {},
      };

      // Build regions mapping (shared)
      const allRegionValues = (fieldsData.regions || []).map((r) => r.value);
      const allPlantsByRegion = fieldsData.plants || {};
      if (
        currentRequest.region.length === allRegionValues.length &&
        allRegionValues.length > 0
      ) {
        allRegionValues.forEach((regionName) => {
          const plantsForRegion = (allPlantsByRegion[regionName] || []).map(
            (plantObj) => plantObj.value.split("-")[0],
          );
          if (plantsForRegion.length > 0) {
            payload.regions[regionName] = plantsForRegion;
          }
        });
      } else {
        currentRequest.region.forEach((regionName) => {
          const plantsForRegion = (fieldsData.plants[regionName] || [])
            .filter((plantObj) => currentRequest.plant.includes(plantObj.value))
            .map((plantObj) => plantObj.value.split("-")[0]);
          if (plantsForRegion.length > 0) {
            payload.regions[regionName] = plantsForRegion;
          }
        });
      }

      let res;
      if (currentRequest.applicationType === "Command Center") {
        // Add Command Center-specific fields
        const resolvedRole =
          typeof selectedRole === "string"
            ? selectedRole
            : selectedRole?.roleName;
        payload = {
          ...payload,
          country: selectedCountry?.label || null,
          userTeam:
            typeof selectedTeam === "string"
              ? selectedTeam
              : selectedTeam?.teamName,
          userRole: resolvedRole,
          confidentialAccess: currentRequest.confidentialAccess,
          roles: resolvedRole ? [resolvedRole] : currentRequest.roles,
        };
        // multipart/form-data
        const formData = new FormData();

        // JSON part
        formData.append(
          "requestData",
          new Blob([JSON.stringify(payload)], { type: "application/json" }),
        );

        // File part (ONLY if NDA accepted)
        if (ndaAccepted) {
          const pdfBytes = await generateNdaPdf();
          formData.append(
            "ndaPdf",
            new Blob([pdfBytes], { type: "application/pdf" }),
            "NDAForm.pdf",
          );
        }
        res = await raiseAccessRequest(
          formData,
          currentRequest.applicationType,
        );
      } else if (isSafetyStockLikeApp) {
        const roleOptionsForApi =
          currentRequest.applicationType === "MP - AL Contract Automation"
            ? AL_CONTRACT_MANAGEMENT_ROLES
            : currentRequest.applicationType ===
              "MP - AL Supplier Collaboration" ||
              currentRequest.applicationType ===
              "MP - AL Supplier Collaboration/Confirmation" ||
              currentRequest.applicationType === "MP - Supplier Confirmation"
              ? AL_SUPPLIER_COLLABORATION_ROLES
              : currentRequest.applicationType ===
                "MP - GR Processing Time Analytics"
                ? GRPT_ROLES
                : currentRequest.applicationType === "MP - Lead time Analytics"
                  ? LEAD_TIME_ANALYTICS_ROLES
                  : currentRequest.applicationType === "MP - AL PO Management"
                    ? PO_MAINTENANCE_ROLES
                    : currentRequest.applicationType ===
                      "CS - Inventory Rebalancing"
                      ? INVENTORY_REBALANCING_ROLES
                      : fieldsData.roles || [];

        const resolvedRoles =
          isAlSupplierCollaborationApp
            ? (currentRequest.roles || []).map((roleValue) =>
              resolveRoleNameForApi(roleValue, roleOptionsForApi),
            )
            : (currentRequest.roles || []).map((roleValue) => {
              if (
                currentRequest.applicationType === "MP - AL Contract Automation" &&
                roleValue === "CM IT Support"
              ) {
                return "CM IT Support";
              }
              if (
                currentRequest.applicationType === "MP - AL Contract Automation" &&
                roleValue === "CD VIEWER"
              ) {
                return "CM VIEWER";
              }
              if (
                currentRequest.applicationType === "MP - Lead time Analytics" &&
                roleValue === "LT IT Support"
              ) {
                return "LT IT Support";
              }
              if (
                currentRequest.applicationType === "MP - Lead time Analytics" &&
                roleValue === "LT VIEWER"
              ) {
                return "LT VIEWER";
              }
              return resolveRoleNameForApi(roleValue, roleOptionsForApi);
            });
        payload = {
          ...payload,
          roles: resolvedRoles,
          ...(isDataTrustAI && !isDataTrustAIAdmin && { userTeam: selectedStaticTeam }),
        };

        // Special payload handling for OM - Load Optimization Bottler role
        if (
          currentRequest.applicationType === "OM - Load Optimization" &&
          currentRequest.roles?.includes("Bottler")
        ) {
          payload = {
            ...payload,
            plant: currentRequest.plant?.[0]
              ? currentRequest.plant[0].split("-")[0]
              : "",
            bottlerName: currentRequest.bottlingGroup?.[0].split("-")[1] || "",
          };
        }
        res = await raiseAccessRequest(payload, currentRequest.applicationType);
      } else {
        // Fallback for other types
        res = await raiseAccessRequest(payload, currentRequest.applicationType);
      }

      if (res?.status === 200 || res?.success) {
        setSnackbarMessage(res?.message || "Request Submitted Successfully");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        handleReset(); // Reset the form after successful submit
      } else {
        setSnackbarMessage(res?.message || "Error Submitting Request");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error("Error submitting access request:", err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      setSnackbarMessage(
        apiMessage || "Something went wrong while submitting request",
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentRequest((prev) => ({
      ...initialRequestState,
      applicationType: prev.applicationType,
    }));

    setNdaDecision("Not Accepted");
    setNdaAccepted(false);
    setShowPreviewSummary(false);

    setSelectedCountry(null);
    setSelectedTeam(null);
    setTeamInputValue("");
    setSelectedRole(null);
    setSelectedStaticTeam("")

    setRequestForType("self");
    setRequestedFor("");
    setRequestedForUserName("");
    setEmailError("");
  };

  const hasBottlingGroups = (fieldsData.bottlingGroups || []).length > 0;

  return (
    <>
      {submitting && <SectionSpinner source="AccessRequestForm" />}
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader={true}
        headerName={""}
        hideTitle={true}
        roles={roles}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          minHeight: "100vh",
          // width: "100%",
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            my: isSopApp ? 0 : 4,
            width: "100%",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          <Box
            mt={0}
            mb={0}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2.5,
              width: { xs: "100%", sm: INPUT_WIDTH },
              maxWidth: "100%",
              position: "relative",
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="application-label">Application</InputLabel>
              <Select
                labelId="application-label"
                id="application"
                value={currentRequest.applicationType}
                label="Application"
                onChange={(e) => {
                  const app = e.target.value;
                  handleReset();
                  setCurrentRequest((prev) => ({
                    ...prev,
                    applicationType: app,
                  }));
                  setSelectedApplication(app);
                }}
                sx={{ color: "#000" }}
              >
                {(fieldsData.applications || []).map((app) => (
                  <MenuItem key={app} value={app}>
                    {app}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!isSopApp && currentRequest.applicationType.length !== 0 && <><Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                //alignSelf: "flex-start",
              }}
            >
              <Typography variant="body1" fontWeight={700} mb={0} color="#000">
                Requesting access for
              </Typography>
            </Box>
              <RadioGroup
                row
                value={requestForType}
                onChange={(e) => {
                  setRequestForType(e.target.value);
                  if (e.target.value === "self") {
                    setRequestedFor("");
                    setRequestedForUserName("");
                    setEmailError("");
                    setCurrentRequest((prev) => ({
                      ...prev,
                      requestedFor: null,
                      requestedForUserName: null,
                    }));
                  }
                }}
              >
                <FormControlLabel
                  value="self"
                  control={<Radio size="small" />}
                  label="Self"
                />
                <FormControlLabel
                  value="other"
                  control={<Radio size="small" />}
                  label="Other"
                />
              </RadioGroup>

              {!isSopApp && requestForType === "other" && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      alignSelf: "flex-start",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      mb={0}
                      color="#000"
                    >
                      Enter details of the user you are requesting access for
                    </Typography>
                    <Tooltip title={onBehalfInfoMessage} placement="right">
                      <IconButton
                        size="small"
                        aria-label="Requested for info"
                        sx={{ p: 0.25 }}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Autocomplete
                    freeSolo
                    fullWidth
                    size="small"
                    options={userOptions}
                    loading={userLoading}
                    noOptionsText="Type to search or add user"
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      if (option.inputValue) return option.inputValue;
                      return option.userName;
                    }}
                    filterOptions={(options, params) => {
                      const filtered = userFilter(options, params);
                      const { inputValue } = params;

                      const isExisting = options.some(
                        (opt) =>
                          opt.userName?.toLowerCase() === inputValue.toLowerCase()
                      );

                      if (inputValue.length >= 2 && !isExisting) {
                        filtered.push({
                          inputValue,
                          userName: `Add "${inputValue}"`,
                          userEmail: "",
                          isNewUser: true,
                        });
                      }

                      return filtered;
                    }}
                    onInputChange={(e, value, reason) => {
                      if (reason !== "input") return;
                      if (!value || value.length < 2) {
                        setUserOptions([]);
                        return;
                      }
                      debouncedFetchUsers(value);
                    }}
                    onChange={(e, newValue) => {
                      if (!newValue) return;
                      if (newValue.inputValue || newValue.isNewUser) {
                        setRequestedForUserName(newValue.inputValue);
                        setRequestedFor("");
                        return;
                      }
                      //Existing user
                      setRequestedForUserName(newValue.userName);
                      setRequestedFor(newValue.userEmail);

                      setCurrentRequest((prev) => ({
                        ...prev,
                        requestedForUserName: newValue.userName,
                        requestedFor: newValue.userEmail,
                      }));
                    }}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          {option.inputValue ? (
                            <Typography
                              variant="body2"
                              sx={{ fontStyle: "italic", color: "primary.main" }}
                            >
                              + Add "{option.inputValue}"
                            </Typography>
                          ) : (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {option.userName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.userEmail}
                              </Typography>
                            </Box>
                          )}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User Name"
                        placeholder="Search or add user"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {userLoading && <CircularProgress size={18} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    value={requestedFor}
                    onChange={handleRequestedForChange}
                    placeholder="Enter Email"
                    error={!!emailError}
                    helperText={emailError}
                  />
                </>
              )}</>}
          </Box>
          {
            (!isSopApp && currentRequest.applicationType.length !== 0) && (
              <>
                {!isSafetyStockLikeApp && (
                  <>
                    <Typography variant="body1" fontWeight={700} mb={0} color="#000">
                      Team & Role
                    </Typography>

                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      alignItems="center"
                      mb={0}
                      sx={{ width: INPUT_WIDTH }}
                    >
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={countryOptions}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.code === value?.code
                        }
                        value={selectedCountry}
                        onChange={(event, newValue) => setSelectedCountry(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Country"
                            size="small"
                            placeholder="Select a country"
                          />
                        )}
                      />
                      {/* Teams – single select with "Add new" capability */}
                      <Autocomplete
                        freeSolo
                        fullWidth
                        size="small"
                        options={teamsRolesData}
                        noOptionsText="Type to search or add a new team"
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;
                          if (option.inputValue) return option.inputValue;
                          return option.teamName;
                        }}
                        filterOptions={(options, params) => {
                          const filtered = teamFilter(options, params);
                          const { inputValue } = params;
                          const isExisting = options.some(
                            (opt) =>
                              opt.teamName?.toLowerCase() ===
                              inputValue.toLowerCase(),
                          );
                          if (inputValue !== "" && !isExisting) {
                            filtered.push({
                              inputValue,
                              teamName: `Add "${inputValue}"`,
                              teamID: null,
                              roles: [],
                            });
                          }
                          return filtered;
                        }}
                        isOptionEqualToValue={(option, value) => {
                          if (typeof value === "string")
                            return option.teamName === value;
                          if (value?.teamID != null)
                            return option.teamID === value.teamID;
                          return option.teamName === value?.teamName;
                        }}
                        value={selectedTeam}
                        inputValue={teamInputValue}
                        onInputChange={(event, newInputValue) => {
                          setTeamInputValue(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          if (newValue?.inputValue) {
                            setSelectedTeam(newValue.inputValue);
                            setTeamInputValue(newValue.inputValue);
                          } else {
                            setSelectedTeam(newValue);
                            setTeamInputValue(newValue.inputValue ?? "");
                          }
                          setSelectedRole(null);
                        }}
                        renderOption={(props, option) => {
                          const { key, ...rest } = props;
                          return (
                            <li key={key} {...rest}>
                              {option.inputValue ? (
                                <Typography
                                  variant="body2"
                                  sx={{ fontStyle: "italic", color: "primary.main" }}
                                >
                                  + Add "{option.inputValue}"
                                </Typography>
                              ) : (
                                option.teamName
                              )}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Team"
                            size="small"
                            placeholder="Search or add a new team"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {selectedTeam &&
                                    typeof selectedTeam === "string" && (
                                      <Chip
                                        label="New"
                                        color="secondary"
                                        size="small"
                                        sx={{ mr: 1 }}
                                      />
                                    )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />

                      {/* Role – single select from available roles; cascades on selected team */}
                      <Autocomplete
                        fullWidth
                        size="small"
                        disabled={!selectedTeam}
                        options={availableRoles}
                        noOptionsText="No roles available"
                        getOptionLabel={(option) => option?.roleName || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.roleName === value?.roleName
                        }
                        value={selectedRole}
                        onChange={(event, newValue) => {
                          setSelectedRole(newValue);
                        }}
                        renderOption={(props, option) => {
                          const { key, ...rest } = props;
                          return (
                            <li key={key} {...rest}>
                              {option.roleName}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Role"
                            size="small"
                            placeholder={
                              !selectedTeam ? "Select a team first" : "Select a role"
                            }
                          />
                        )}
                      />
                    </Box>
                  </>
                )}

                {isSafetyStockLikeApp && (
                  <>
                    <Typography variant="body1" fontWeight={700} mb={0} color="#000">
                      Role
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      alignItems="center"
                      mb={0}
                    >
                      <FormControl size="small" sx={{ width: INPUT_WIDTH }}>
                        <InputLabel id="safety-role-label">Role</InputLabel>
                        <Select
                          labelId="safety-role-label"
                          id="safety-role"
                          value={currentRequest.roles?.[0] || ""}
                          label="Role"
                          onChange={(e) => {
                            const selectedRole = e.target.value;
                            setSelectedRole(selectedRole);
                            setCurrentRequest((prev) => {
                              const newRequest = {
                                ...prev,
                                roles: [selectedRole],
                                region: [],
                                plant: [],
                              };
                              const adminRoles = ["it support", "ssa admin"];
                              if (
                                adminRoles.includes(selectedRole.trim().toLowerCase())
                              ) {
                                const allRegions = (fieldsData.regions || []).map(
                                  (r) => r.value,
                                );
                                const allPlants = Object.values(
                                  fieldsData.plants || {},
                                )
                                  .flat()
                                  .map((p) => p.value);
                                newRequest.region = allRegions;
                                newRequest.plant = allPlants;
                              }

                              if (
                                (currentRequest.applicationType ===
                                  "MP - AL Supplier Collaboration" ||
                                  currentRequest.applicationType ===
                                  "MP - AL Supplier Collaboration/Confirmation" ||
                                  currentRequest.applicationType ===
                                  "MP - Supplier Confirmation") &&
                                AL_SUPPLIER_ALL_ACCESS_ROLES.has(selectedRole)
                              ) {
                                const allRegions = (fieldsData.regions || []).map(
                                  (r) => r.value,
                                );
                                const allPlants = Object.values(
                                  fieldsData.plants || {},
                                )
                                  .flat()
                                  .map((p) => p.value);
                                newRequest.region = allRegions;
                                newRequest.plant = allPlants;
                              }

                              if (
                                currentRequest.applicationType ===
                                "MP - Lead time Analytics" &&
                                LEAD_TIME_GLOBAL_ROLES.has(selectedRole)
                              ) {
                                const allRegions = (fieldsData.regions || []).map(
                                  (r) => r.value,
                                );
                                const allPlants = Object.values(
                                  fieldsData.plants || {},
                                )
                                  .flat()
                                  .map((p) => p.value);
                                newRequest.region = allRegions;
                                newRequest.plant = allPlants;
                              }

                              if (
                                currentRequest.applicationType ===
                                "MP - AL PO Management" &&
                                PO_MAINTENANCE_GLOBAL_ROLES.has(selectedRole)
                              ) {
                                const allRegions = (fieldsData.regions || []).map(
                                  (r) => r.value,
                                );
                                const allPlants = Object.values(
                                  fieldsData.plants || {},
                                )
                                  .flat()
                                  .map((p) => p.value);
                                newRequest.region = allRegions;
                                newRequest.plant = allPlants;
                              }

                              if (
                                currentRequest.applicationType ===
                                "CS - Inventory Rebalancing" &&
                                INVENTORY_REBALANCING_GLOBAL_ROLES.has(selectedRole)
                              ) {
                                const allRegions = (fieldsData.regions || []).map(
                                  (r) => r.value,
                                );
                                const allPlants = Object.values(
                                  fieldsData.plants || {},
                                )
                                  .flat()
                                  .map((p) => p.value);
                                newRequest.region = allRegions;
                                newRequest.plant = allPlants;
                              }

                              if (
                                currentRequest.applicationType ===
                                "OM - Load Optimization" &&
                                OM_TLO_GLOBAL_ROLES.has(selectedRole)
                              ) {
                                const allRegions = (fieldsData.regions || []).map(
                                  (r) => r.value,
                                );
                                const allPlants = Object.values(
                                  fieldsData.plants || {},
                                )
                                  .flat()
                                  .map((p) => p.value);
                                newRequest.region = allRegions;
                                newRequest.plant = allPlants;
                              }

                              return newRequest;
                            });
                          }}
                        >
                          {activeRoleOptions.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </>
                )}
                {isDataTrustAI && !isDataTrustAIAdmin && (
                  <>
                    <Typography variant="body1" fontWeight={700} mb={0} color="#000">
                      Team
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      alignItems="center"
                      mb={0}
                    >
                      <FormControl size="small" sx={{ width: INPUT_WIDTH }}>
                        <InputLabel id="dtai-team-label">Team</InputLabel>
                        <Select
                          labelId="dtai-team-label"
                          id="dtai-team"
                          value={selectedStaticTeam}
                          label="Team"
                          onChange={(e) => setSelectedStaticTeam(e.target.value)}
                        >
                          {STATIC_TEAM_OPTIONS.map((t) => (
                            <MenuItem key={t.teamID} value={t.teamName}>
                              {t.teamName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </>
                )}
                {!(isDataTrustAI && isDataTrustAIAdmin) && (
                  <>          <Typography variant="body1" fontWeight={700} mb={0} color="#000">
                    Access Level
                  </Typography>

                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      alignItems="center"
                      mb={0}
                    >

                      <MultiSelectCheckbox
                        label="Region"
                        value={region}
                        options={fieldsData.regions}
                        onChange={handleMultiSelectChange("region")}
                        disabled={
                          isAdminRole ||
                          isAlContractGlobalRole ||
                          isAlSupplierAllAccessRole ||
                          isGrptAllAccessRole ||
                          isLeadTimeGlobalRole ||
                          isPoMaintenanceGlobalRole ||
                          isInventoryRebalancingGlobalRole ||
                          isOmTloGlobalRole
                        }
                        placeholder="Any Region"
                        width={INPUT_WIDTH}
                      />

                      {!hidePlantDropdownForAlSupplier && (
                        <MultiSelectCheckbox
                          label="Plant"
                          value={plant}
                          options={availablePlants}
                          onChange={handleMultiSelectChange("plant")}
                          disabled={
                            isAdminRole ||
                            region.length === 0 ||
                            isAlContractGlobalRole ||
                            isAlSupplierAllAccessRole ||
                            isGrptAllAccessRole ||
                            isGrptRegionalAutoPlantsRole ||
                            isLeadTimeGlobalRole ||
                            isPoMaintenanceGlobalRole ||
                            isInventoryRebalancingGlobalRole ||
                            isOmTloGlobalRole ||
                            (isAlContractManagementApp && !canSelectPlantsManuallyForAl) ||
                            (isIoSafetyStockApp &&
                              region.length === (fieldsData.regions || []).length &&
                              (fieldsData.regions || []).length > 0)
                          }
                          placeholder="Any Plant"
                          width={INPUT_WIDTH}
                        />
                      )}

                      {isOmTloBottlerRole && (
                        <FormControl
                          size="small"
                          fullWidth
                          disabled={region.length === 0 || plant.length === 0}
                          sx={{ width: INPUT_WIDTH }}
                        >
                          <InputLabel id="bottling-group-label">
                            Bottling Group
                          </InputLabel>

                          <Select
                            labelId="bottling-group-label"
                            value={currentRequest.bottlingGroup?.[0] || ""}
                            label="Bottling Group"
                            onChange={(e) => {
                              setCurrentRequest((prev) => ({
                                ...prev,
                                bottlingGroup: e.target.value ? [e.target.value] : [],
                              }));
                            }}
                          >
                            {(fieldsData.bottlingGroups || []).map((group) => (
                              <MenuItem key={group.value} value={group.value}>
                                {group.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </>)
                }
                {!isSafetyStockLikeApp && requestForType !== "other" && (
                  <>
                    <Typography variant="body1" fontWeight={700} mb={0} color="#000">
                      Confidential Level
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={2}
                    >
                      <Button
                        variant="contained"
                        onClick={() => setOpenNdaModal(true)}
                        disabled={
                          requestedForUserName.trim().length > 0 ||
                          requestedFor.trim().length > 0
                        }
                        sx={{
                          backgroundColor: "#4b4b4bff",
                          color: "#fff",
                          height: 30,
                          "&:hover": { backgroundColor: "#2d2d2dff", color: "#fff" },
                          "&.Mui-disabled": {
                            color: "#fff",
                            opacity: 0.7,
                          },
                        }}
                      >
                        View Non Disclosure Agreement (NDA)
                      </Button>
                      {ndaDecision === "Accepted" && (
                        <Chip
                          icon={<DoneIcon fontSize="small" />}
                          label="NDA ACCEPTED"
                          color="success"
                          size="medium"
                        />
                      )}
                      {ndaDecision === "Declined" && (
                        <Chip
                          icon={<PriorityHighIcon fontSize="small" />}
                          label="NDA DECLINED"
                          color="error"
                          variant="filled"
                        />
                      )}
                    </Box>
                    <Box mb={1}>
                      <MultiSelectCheckbox
                        label="Confidential"
                        value={confidentialAccess}
                        options={fieldsData.confidentialFields}
                        onChange={handleMultiSelectChange("confidentialAccess")}
                        placeholder="No Confidential Access"
                        disabled={!ndaAccepted}
                        width={INPUT_WIDTH}
                      />
                    </Box>
                  </>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePreview}
                    disabled={
                      (isOmTruckLoadApp
                        ? (!currentRequest.roles?.[0] ||
                          (!isOmTloNoRegionPlantRole && (plant.length === 0 || region.length === 0)))
                        : isDataTrustAI ? !currentRequest?.roles[0] : ((plant.length === 0 || region.length === 0) && confidentialAccess.length === 0)
                      ) ||
                      !!emailError
                    }
                    sx={{
                      backgroundColor: "#000000ff",
                      color: "#fff",
                      height: 30,
                      "&:hover": { backgroundColor: "#2d2d2dff", color: "#fff" },
                      "&.Mui-disabled": {
                        color: "#fff",
                        opacity: 0.85,
                      },
                    }}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleReset}
                    disabled={
                      isDataTrustAI ? !currentRequest?.roles[0] :
                        !isOmTloNoRegionPlantRole &&
                        (plant.length === 0 ||
                          region.length === 0) /*|| mrpController.length === 0*/ &&
                        confidentialAccess.length === 0
                    }
                    sx={{
                      backgroundColor: "#000000ff",
                      color: "#fff",
                      height: 30,
                      "&:hover": { backgroundColor: "#2d2d2dff", color: "#fff" },
                      "&.Mui-disabled": {
                        color: "#fff",
                        opacity: 0.85,
                      },
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                      currentRequest.applicationType.length === 0 || (isDataTrustAI && !isDataTrustAIAdmin && !selectedStaticTeam) ||
                      (isOmTruckLoadApp
                        ? (!currentRequest.roles?.[0] ||
                          (!isOmTloNoRegionPlantRole && (plant.length === 0 || region.length === 0 ||
                            (isOmTloBottlerRole &&
                              (!hasBottlingGroups ||
                                (currentRequest.bottlingGroup?.length || 0) === 0)))))
                        : isDataTrustAI ? !currentRequest.roles?.[0] : ((plant.length === 0 || region.length === 0) && confidentialAccess.length === 0)
                      ) ||
                      !!emailError
                    }
                    sx={{
                      backgroundColor: "#000000ff",
                      color: "#fff",
                      height: 30,
                      "&:hover": { backgroundColor: "#2d2d2dff", color: "#fff" },
                      "&.Mui-disabled": {
                        color: "#fff",
                        opacity: 0.85,
                      },
                    }}
                  >
                    Submit
                  </Button>
                  {/* <Button
              variant="outlined"
              onClick={previewNdaPdf}
              disabled={!ndaAccepted}
            >
              Preview NDA PDF
            </Button> */}
                </Box>
                {showPreviewSummary && (
                  <Paper
                    sx={{
                      p: 2,
                      mt: 3,
                      mb: 2,
                      backgroundColor: "#cdcacaff",
                      border: "1px dashed #E5E7EB",
                      position: "relative",
                    }}
                  >
                    {/* Close Icon */}
                    <IconButton
                      size="small"
                      onClick={() => setShowPreviewSummary(false)}
                      sx={{ position: "absolute", top: 4, right: 4, color: "#333" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#1A535C"
                      mb={1}
                    >
                      Preview Summary:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-line", color: "#333" }}
                    >
                      {previewSummary}
                    </Typography>
                  </Paper>
                )}
                <Dialog
                  open={openNdaModal}
                  onClose={() => setOpenNdaModal(false)}
                  maxWidth="md"
                  fullWidth
                >
                  <DialogTitle
                    sx={{
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                    }}
                  >
                    Non‑Disclosure Agreement (NDA)
                    <IconButton
                      onClick={() => setOpenNdaModal(false)}
                      size="small"
                      sx={{ color: "#555" }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent dividers sx={{ maxHeight: "60vh" }}>
                    <Typography variant="body2" paragraph>
                      This Non-Disclosure Agreement <strong>(Agreement)</strong> is
                      entered into effective as of <strong>{today} </strong>
                      between{" "}
                      <strong>
                        {" "}
                        The Coca-Cola Company located at One Coca-Cola Plaza NW,
                        Atlanta, GA 30313
                      </strong>{" "}
                      and <strong>{username}</strong>, each of which will be referred
                      to as a Party and collectively as Parties. The term “Party” will
                      include an affiliate of that Party. For purposes of this
                      Agreement, an “Affiliate” of a Party means
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>direct and indirect subsidiaries;</li>
                        <li>
                          any entity that such Party directly, or indirectly through
                          one or more intermediaries, controls, or is controlled by or
                          is under common control with; or
                        </li>
                        <li>
                          in the case of TCCC, any bottler with whom TCCC has a
                          license, alliance, or distribution agreement and any
                          wholly-owned subsidiary thereof.
                        </li>
                      </ol>
                    </Typography>

                    <Typography variant="body2" paragraph>
                      The Parties may disclose or share with each other information,
                      documents, and other confidential or proprietary matters of the
                      disclosing Party (Information) in connection with{" "}
                      <strong>Confidential Information</strong>.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      The following Information will be considered{" "}
                      <strong>Confidential Information</strong>:
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>
                          Information that the disclosing Party marks or designates as
                          confidential;
                        </li>
                        <li>
                          Information that is not marked or designated as being
                          confidential, but that under the circumstances a reasonable
                          person would believe to be confidential;
                        </li>
                        <li>
                          the fact that this Agreement exists and that the Parties are
                          cooperating with each other for the Purpose; and
                        </li>
                        <li>
                          any notes, analyses, compilations, studies, summaries, and
                          other materials, however documented, containing or based, in
                          whole or in part, on any Confidential Information.
                        </li>
                      </ol>
                    </Typography>

                    <Typography variant="body2" paragraph>
                      Each Party receiving Confidential Information will:
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>
                          restrict the disclosure of Confidential Information only to
                          its employees who have a need to know the Confidential
                          Information to achieve the Purpose;
                        </li>
                        <li>
                          not disclose the Confidential Information to any third
                          party; provided, however, that each Party may disclose
                          Confidential Information to its affiliates, directors,
                          attorneys, and financial advisors who:
                          <ol
                            type="i"
                            style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                          >
                            <li>
                              need to know the Confidential Information to assist the
                              Party, or act on its behalf, in relation to the Purpose
                              or to exercise its rights under this Agreement;
                            </li>
                            <li>
                              are informed by the Party of the confidential nature of
                              the Confidential Information; and
                            </li>
                            <li>
                              are subject to confidentiality duties or obligations to
                              the Party that are no less restrictive than the terms
                              and conditions contained in this Agreement;
                            </li>
                          </ol>
                          provided, however, that the Party will be responsible for
                          any breach of this Agreement caused by them;
                        </li>

                        <li>
                          only use the Confidential Information for the Purpose;
                        </li>

                        <li>
                          return all tangible Information (including any copies
                          thereof), and erase all electronic or other intangible forms
                          of the Information, within 30 days of the receipt of a
                          written request from the disclosing Party to do so,
                          provided, however, that the receiving Party may retain one
                          archival copy to the extent required to comply with its
                          legal obligations; and
                        </li>

                        <li>
                          give prompt written notice to the disclosing Party of any
                          unauthorized use or disclosure of the Confidential
                          Information, and reasonably assist the disclosing Party in
                          remedying each unauthorized use or disclosure.
                        </li>
                      </ol>
                    </Typography>

                    <Typography variant="body2" paragraph>
                      The obligations in Section 4 above will not apply to
                      Confidential Information that:
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>
                          is, or subsequently becomes, available to the public through
                          no breach of the receiving Party’s obligations hereunder;
                        </li>
                        <li>
                          the receiving Party can show that such Information was
                          previously known to it as a matter of record at the time of
                          receipt;
                        </li>
                        <li>
                          is subsequently and lawfully obtained from a third party who
                          has obtained the Confidential Information through no breach
                          of the receiving Party’s obligations hereunder; or
                        </li>
                        <li>
                          is disclosed to a third party by the disclosing Party
                          without a corresponding obligation of confidentiality.
                        </li>
                      </ol>
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Additionally, a receiving Party will not be in breach of its
                      obligations if it discloses Confidential Information as required
                      by a government agency or by operation of law; provided however
                      that the receiving Party (to the extent permitted by applicable
                      law):
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>
                          notifies the disclosing Party prior to disclosure to enable
                          the disclosing Party to seek to oppose or restrict the
                          disclosure;
                        </li>
                        <li>
                          reasonably cooperates with any such attempt by the
                          disclosing Party to oppose or restrict the disclosure or to
                          seek a protective order; and
                        </li>
                        <li>
                          only discloses such Confidential Information that is
                          required to be disclosed by applicable law.
                        </li>
                      </ol>
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Each Party agrees not to publish or use any advertising, sales
                      promotion, mailing or publicity matter that mentions or implies
                      the names of the other Party or its affiliates or authorized
                      bottlers without the prior written consent of the other Party.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      No agreement regarding the Purpose will be deemed to exist
                      unless and until a definitive written agreement regarding the
                      Purpose has been signed by each of the Parties. Neither Party is
                      obligated to enter into a definitive written agreement, and
                      neither Party is precluded from considering the same or similar
                      goods or services from third parties. Neither Party will be
                      obligated to:
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>
                          pay or otherwise provide a benefit to the other Party;
                        </li>
                        <li>
                          take or refrain from taking any action, except as expressly
                          set forth in this Agreement; or
                        </li>
                        <li>disclose any Information.</li>
                      </ol>
                    </Typography>

                    <Typography variant="body2" paragraph>
                      Each disclosing Party represents that it has the lawful right to
                      disclose any Information that it discloses to the receiving
                      Party. Each Party makes no further representations of any kind
                      with respect to any Information that it discloses, and any such
                      Information is provided on an “as is” basis.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      This Agreement and its interpretation, and any disputes related
                      to or arising out of this Agreement, will be governed by the
                      laws of the State of Georgia. The courts in the city of Atlanta,
                      Georgia having jurisdiction will adjudicate any such disputes.
                      Each party hereby irrevocably submits to the exclusive
                      jurisdiction of such courts in any such suit, action or
                      proceeding, and each party consents to the venue of such courts.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      A breach of this Agreement by a receiving Party will cause the
                      disclosing Party irreparable injury for which monetary damages
                      will not provide adequate compensation and, in addition to any
                      other remedy, the disclosing Party will be entitled to seek
                      injunctive relief against such breach or threatened breach
                      without proving actual damages.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      Each Party, in connection with this Agreement, will not engage
                      with:
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>
                          the government of, or any entity, group or individual
                          within, any country that is the target of any laws
                          administered by the U.S. Office of Foreign Assets Control,
                          Department of the Treasury (OFAC) or any other governmental
                          entity around the world imposing economic sanctions
                          (collectively Embargoed Country); and
                        </li>
                        <li>
                          any government, entity, group, or individual who is named on
                          the OFAC List of Specially Designated Nationals and Blocked
                          Persons or other similar lists maintained by any
                          governmental entity (collectively Sanctioned Party).
                        </li>
                      </ol>
                    </Typography>

                    <Typography variant="body2" paragraph>
                      Each Party represents and warrants that it is not:
                      <ol
                        type="a"
                        style={{ paddingLeft: "2rem", marginTop: "0.5rem" }}
                      >
                        <li>a Sanctioned Party;</li>
                        <li>
                          owned or controlled by, or acting for or on behalf of, a
                          Sanctioned Party; or
                        </li>
                        <li>
                          directly or indirectly owned, controlled by, or acting on
                          behalf of an Embargoed Country.
                        </li>
                        Should a Party become designated a Sanctioned Party, or become
                        associated, controlled, or owned by a Sanctioned Party or
                        Embargoed Country, any agreement between the Parties will
                        terminate automatically.
                      </ol>
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Neither Party will be entitled to assign this Agreement to a
                      third party.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      This Agreement may be executed in any number of counterparts
                      (including without limitation signature via DocuSign or similar
                      e-signature service or by facsimile transmission), each of which
                      will be deemed an original, but all of which taken together will
                      constitute one single agreement between the Parties. The person
                      signing on behalf of each Party represents that he or she is
                      authorized to execute this Agreement on behalf of such Party and
                      has the authority to bind such Party to the terms and conditions
                      of this Agreement.
                    </Typography>
                    <Typography variant="body2" paragraph>
                      This Agreement represents the entire agreement of the Parties
                      and it supersedes all prior communications, agreements, and
                      understandings between the Parties relating to the subject
                      matter hereof. This Agreement may not be modified, amended, or
                      waived except by a written agreement signed by each Party. If a
                      court of competent jurisdiction determines that a provision of
                      this Agreement is unenforceable, this Agreement will be
                      interpreted as if such unenforceable provision is not part of
                      this Agreement.
                    </Typography>

                    <Typography variant="body2" paragraph>
                      The initial term of this Agreement will be for a term of three
                      (3) years. Thereafter, the term of this Agreement will renew for
                      successive one-year periods until terminated by either Party.
                      Either Party may terminate this Agreement by providing at least
                      30 days’ prior written notice to the individual signing on
                      behalf of the other Party. Sections 4, 5, 6, 7 and 8 will
                      survive termination of this Agreement and will continue until
                      the information is no longer considered Confidential
                      Information; provided however that with respect to any
                      Confidential Information that constitutes a trade secret,
                      Sections 4, 5, 6, 7, and 8 will continue to apply until such
                      time as that Confidential Information is no longer protected as
                      a trade secret under applicable law.
                    </Typography>
                  </DialogContent>
                  {/* <DialogContent dividers sx={{ maxHeight: "60vh" }}>
  {NDA_CONTENT({ today, username })}
</DialogContent> */}

                  <DialogActions>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setNdaAccepted(false);
                        setNdaDecision("Declined");
                        setOpenNdaModal(false);
                      }}
                    >
                      Decline
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      sx={{
                        color: "#fff",
                        "&:hover": { color: "#fff" },
                      }}
                      onClick={() => {
                        setNdaAccepted(true);
                        setNdaDecision("Accepted");
                        setOpenNdaModal(false);
                      }}
                    >
                      Accept
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )
          }

          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
        {currentRequest.applicationType === "S&OP - Sales And Operations Planning" ? (
          <AccessDetails
            requestForType={requestForType}
            setRequestForType={setRequestForType}
            email={email}
            username={username}
            applications={fieldsData.applications || []}
            applicationType={currentRequest.applicationType}
            onApplicationChange={(nextValue) => {
              handleReset();
              setCurrentRequest((prev) => ({
                ...prev,
                applicationType: nextValue,
              }));
            }}
          />
        ) : null}
      </Box>
    </>
  );
};

export default AccessRequestForm;
